import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import TileEditorModal from './TileEditorModal';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Popup from './Popup';
import { getAccountType } from '../context/accountUtils';

const HEADER_HEIGHT = 56;
const minTileSize = 125;
const minTileGap = 10;

const Tile = ({ tile, index, swapTiles, onEdit, tileWidth, tileHeight }) => {
  const [, ref] = useDrag({ type: 'tile', item: { id: tile.id, index } });
  const [, drop] = useDrop({
    accept: 'tile',
    drop: (draggedItem) => {
      if (draggedItem.index !== index) {
        swapTiles(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => ref(drop(node))}
      onClick={() => onEdit(tile)}
      style={{
        backgroundColor: tile.color || '#eeeeee',
        borderRadius: tile.is_folder ? '8px 80px 8px 8px' : '8px',
        width: tileWidth,
        height: tileHeight,
        border: '1px solid #ccc',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        cursor: 'move',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {tile.image_url && (
        <img
          src={tile.image_url}
          alt={tile.label}
          style={{
            width: 58,
            height: 58,
            objectFit: 'contain',
            marginBottom: 4,
          }}
          onContextMenu={(e) => e.preventDefault()}
          draggable={false}
        />
      )}
      <span
        style={{
          fontSize: '0.85rem',
          fontWeight: 500,
          textAlign: 'center',
        }}
      >
        {tile.label}
      </span>
    </div>
  );
};

const TrashCan = ({ onDrop }) => {
  const [{ isOver }, drop] = useDrop({
    accept: 'tile',
    drop: (item) => onDrop(item.id),
    collect: (monitor) => ({ isOver: !!monitor.isOver() }),
  });

  return (
    <div
      ref={drop}
      style={{
        position: 'fixed',
        transform: 'translate(-50%, 5px)',
        left: '50%',
        borderRadius: '50%',
        backgroundColor: isOver ? '#e3342f' : '#f8d7da',
        border: '2px dashed #dc3545',
        width: '45px',
        height: '45px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2rem',
        color: '#dc3545',
        transition: '0.2s',
      }}
    >
      🗑️
    </div>
  );
};

const BoardEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isUnlimited = getAccountType() === 'unlimited';
  const [tiles, setTiles] = useState([]);
  const [userBoards, setUserBoards] = useState([]);
  const [showBoardListPopup, setShowBoardListPopup] = useState(false);

  // Header board-name dropdown reuses this existing selector instead of
  // creating a second board-switching implementation.
  useEffect(() => {
    const openFromHeader = () => setShowBoardListPopup(true);
    window.addEventListener('iboard-open-board-selector', openFromHeader);
    return () => window.removeEventListener('iboard-open-board-selector', openFromHeader);
  }, []);
  const [loading, setLoading] = useState(true);
  const [editTile, setEditTile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [boardName, setBoardName] = useState('');
  const [gridSettings, setGridSettings] = useState({
    columns: 4,
    TILE_WIDTH: minTileSize,
    TILE_HEIGHT: minTileSize,
    TILE_GAP: minTileGap,
    cTILE_GAP: minTileGap,
    rTILE_GAP: minTileGap,
  });

  const [showSetupPopup, setShowSetupPopup] = useState(false);
  const [boardConfig, setBoardConfig] = useState({
    name: '',
    columns: 8,
    rows: 5,
    scrollable: false,
    square_buttons: true,
    theme: 'default',
    is_core_board: false,
    core_default_board_id: null,
  });

  const calculateGridSettings = (rows, cols, scrollable, squareButtons) => {
    const windowHeight = window.innerHeight - HEADER_HEIGHT;
    const windowWidth = window.innerWidth;
    const gap = minTileGap;

    let tileWidth, tileHeight, allowScrollY;

    if (scrollable) {
      tileWidth = (windowWidth - gap * (cols + 4)) / cols;
      tileHeight = squareButtons
        ? tileWidth
        : Math.max(tileWidth * 0.5, 80);
      allowScrollY = true;
    } else {
      const maxByWidth = (windowWidth - gap * (cols + 2)) / cols;
      const maxByHeight = (windowHeight - gap * (rows + 2)) / rows;
      if (squareButtons) {
        tileWidth = tileHeight = Math.floor(
          Math.min(maxByWidth, maxByHeight)
        );
      } else {
        tileWidth = maxByWidth;
        tileHeight = maxByHeight;
      }
      allowScrollY = false;
    }

    const columnGap = gap;
    const rowGap = gap;

    tileWidth = Math.round(tileWidth);
    tileHeight = Math.round(tileHeight);

    return {
      columns: cols,
      rows,
      TILE_WIDTH: tileWidth,
      TILE_HEIGHT: tileHeight,
      cTILE_GAP: columnGap,
      rTILE_GAP: rowGap,
      allowScrollY,
    };
  };

  // ✅ Recalculate grid whenever boardConfig changes or window resizes
  useEffect(() => {
    if (!boardConfig) return;

    const updateGrid = () => {
      setGridSettings(
        calculateGridSettings(
          boardConfig.rows,
          boardConfig.columns,
          boardConfig.scrollable,
          boardConfig.square_buttons
        )
      );
    };

    updateGrid();
    window.addEventListener('resize', updateGrid);
    return () => window.removeEventListener('resize', updateGrid);
  }, [boardConfig]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const boardRes = await api.get('/boards.php');
        const allBoards = Array.isArray(boardRes.data) ? boardRes.data : [];
        setUserBoards(allBoards);

        let currentIsCoreBoard = false;
        let currentCoreDefaultBoardId = null;
        if (isUnlimited) {
          try {
            const coreRes = await api.get('/core-settings.php');
            const coreBoards = Array.isArray(coreRes.data?.core_boards) ? coreRes.data.core_boards : [];
            const currentCore = coreBoards.find((b) => String(b.id) === String(id));
            currentIsCoreBoard = Boolean(currentCore);
            currentCoreDefaultBoardId = currentCore?.core_default_board_id ?? null;
          } catch (coreErr) {
            console.debug('Could not load Core Board status:', coreErr);
          }
        }

        const board = allBoards.find((b) => b.id.toString() === id);
        if (board) {
          setBoardName(board.name || `Board #${id}`);

          let cfg = {
            name: board.name || '',
            columns: 8,
            rows: 5,
            scrollable: false,
            square_buttons: true,
            theme: board.theme || 'default',
            is_core_board: currentIsCoreBoard,
            core_default_board_id: currentCoreDefaultBoardId,
          };

          if (board.grid_config) {
            try {
              const parsed = JSON.parse(board.grid_config);
              cfg.columns = parsed.columns || parsed.cols || 8;
              cfg.rows = parsed.rows || 5;
            } catch {
              /* ignore malformed */
            }
          }

          const toBool = (v) =>
            String(v) === '1' || v === 1 || v === true;
          cfg.scrollable = toBool(board.scrollable);
          cfg.square_buttons = toBool(board.square_buttons);
          setBoardConfig(cfg);
        }

        const tileRes = await api.get(`/tiles.php?board_id=${id}`);
        const sorted = tileRes.data.sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0)
        );
        setTiles(sorted);
      } catch (err) {
        console.error('Failed to load board or tiles:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const saveTilePositions = useCallback(async (changedTiles) => {
    if (!navigator.onLine || changedTiles.length === 0) return;

    const results = await Promise.allSettled(
      changedTiles.map((tile) =>
        api.put(`/tiles.php?id=${tile.id}`, {
          position: tile.position,
        })
      )
    );

    const failures = results
      .map((result, index) => ({
        result,
        tile: changedTiles[index],
      }))
      .filter(({ result }) => result.status === 'rejected');

    if (failures.length > 0) {
      console.error(
        'Failed to save tile positions:',
        failures.map(({ tile, result }) => ({
          tileId: tile.id,
          error: result.reason,
        }))
      );
    }
  }, []);

  const swapTiles = useCallback(
    (fromIndex, toIndex) => {
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= tiles.length ||
        toIndex >= tiles.length
      ) {
        return;
      }

      const updated = [...tiles];
      const temp = updated[fromIndex];
      updated[fromIndex] = updated[toIndex];
      updated[toIndex] = temp;

      const reordered = updated.map((tile, idx) => ({
        ...tile,
        position: idx,
      }));

      setTiles(reordered);

      // Only the two swapped tiles have changed position.
      // Keeping the request outside setTiles prevents duplicate requests
      // when React.StrictMode checks state updater purity.
      void saveTilePositions([
        reordered[fromIndex],
        reordered[toIndex],
      ]);
    },
    [tiles, saveTilePositions]
  );

  const deleteTile = async (tileId) => {
    try {
      await api.delete(`/tiles.php?id=${tileId}`);
      setTiles((prev) =>
        prev.map((tile) =>
          tile.id === tileId
            ? {
                ...tile,
                label: '',
                color: '#eeeeee',
                image_url: '',
                is_folder: 0,
                linked_board_id: null,
              }
            : tile
        )
      );
    } catch (err) {
      console.error('Failed to delete tile:', err);
    }
  };

  const handleTileSaved = (updatedTile) => {
    setTiles((prev) => {
      const exists = prev.find((t) => t.id === updatedTile.id);
      return exists
        ? prev.map((t) => (t.id === updatedTile.id ? updatedTile : t))
        : [...prev, updatedTile].map((t, idx) => ({
            ...t,
            position: idx,
          }));
    });
    setShowModal(false);
    setEditTile(null);
  };

  const handleTileDel = async (xtileId) => {
    try {
      await api.delete(`/tiles.php?id=${xtileId}`);
      setTiles((prev) =>
        prev.map((tile) =>
          tile.id === xtileId
            ? {
                ...tile,
                label: '',
                color: '#eeeeee',
                image_url: '',
                is_folder: 0,
                linked_board_id: null,
              }
            : tile
        )
      );
    } catch (err) {
      console.error('Failed to delete tile:', err);
    }
    setShowModal(false);
    setEditTile(null);
  };

  const handleBoardUpdate = async (data) => {
    try {
      const res = await api.post('/resize-board.php', {
        board_id: parseInt(id),
        name: data.name,
        grid_config: data.grid_config,
        scrollable: data.scrollable ? 1 : 0,
        square_buttons: data.square_buttons ? 1 : 0,
        theme: data.theme || 'default',
        is_core_board: isUnlimited && data.is_core_board ? 1 : 0,
        core_default_board_id: isUnlimited && data.is_core_board ? (data.core_default_board_id || null) : null,
      });

      const updatedTiles = Array.isArray(res.data?.tiles)
        ? res.data.tiles.sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0)
          )
        : [];

      setBoardName(data.name);
      setBoardConfig({
        name: data.name,
        columns: data.grid_config.columns,
        rows: data.grid_config.rows,
        scrollable: data.scrollable,
        square_buttons: data.square_buttons,
        theme: data.theme || 'default',
        is_core_board: isUnlimited && Boolean(data.is_core_board),
        core_default_board_id: isUnlimited && data.is_core_board ? (data.core_default_board_id || null) : null,
      });
      setTiles(updatedTiles);
      setShowSetupPopup(false);

      // Invalidate cached board data so the next board/list visit receives
      // the resized board rather than a stale offline copy.
      if (navigator.onLine) {
        try {
          const cache = await caches.open('api-cache');
          await Promise.all([
            cache.delete(`/aac/api/tiles.php?board_id=${id}`),
            cache.delete(`/aac/api/getconfig.php?id=${id}`),
            cache.delete('/aac/api/offline-sync.php'),
            cache.delete('/aac/api/boards.php'),
          ]);
          localStorage.removeItem(`boardCacheAt:${id}`);
        } catch (cacheErr) {
          console.debug('Failed to clear resized board cache:', cacheErr);
        }
      }

      // Let the Header/BoardView refresh their Core Board selectors immediately.
      window.dispatchEvent(new CustomEvent('sticky-core-changed'));
    } catch (err) {
      console.error('Failed to update board:', err);
    }
  };

  const openBoardForEditing = (boardId) => {
    if (!boardId || String(boardId) === String(id)) {
      setShowBoardListPopup(false);
      return;
    }

    setShowBoardListPopup(false);
    setShowModal(false);
    setEditTile(null);
    setShowSetupPopup(false);
    navigate(`/board/${boardId}/edit`);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Loading editor…</p>
      </div>
    );
  }

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div style={{ padding: '1rem', width: '98%', margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1rem',
              marginTop: '-30px',
            }}
          >
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              Edit Board: {boardName}
            </h2>

            <button
              onClick={() => setShowSetupPopup(true)}
              style={{
                background: '#007bff',
                color: '#fff',
                border: 'none',
                padding: '6px 10px',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              ⚙️ Board Setup
            </button>

            <button
              onClick={() => setShowBoardListPopup(true)}
              style={{
                background: '#6c757d',
                color: '#fff',
                border: 'none',
                padding: '6px 10px',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              📋 Board List
            </button>

            <TrashCan onDrop={deleteTile} />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${gridSettings.columns}, ${gridSettings.TILE_WIDTH}px)`,
              columnGap: `${gridSettings.cTILE_GAP}px`,
              rowGap: `${gridSettings.rTILE_GAP}px`,
              height: gridSettings.allowScrollY
                ? 'auto'
                : `calc(100vh - ${HEADER_HEIGHT}px)`,
              maxHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
              justifyContent: 'center',
              alignContent: 'start',
              overscrollBehavior: 'none',
              padding: '10px',
            }}
          >
            {tiles.map((tile, idx) => (
              <Tile
                key={tile.id}
                tile={tile}
                index={idx}
                tileWidth={gridSettings.TILE_WIDTH}
                tileHeight={gridSettings.TILE_HEIGHT}
                swapTiles={swapTiles}
                onEdit={(t) => {
                  setEditTile(t);
                  setShowModal(true);
                }}
              />
            ))}
          </div>

          {showModal && (
            <TileEditorModal
              boardId={parseInt(id)}
              tile={editTile}
              onClose={() => setShowModal(false)}
              onTileSaved={handleTileSaved}
              onTileDel={handleTileDel}
            />
          )}
        </div>
      </DndProvider>

      {showBoardListPopup && (
        <div
          onClick={() => setShowBoardListPopup(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5000,
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(560px, 92vw)',
              maxHeight: '80vh',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                borderBottom: '1px solid #ddd',
              }}
            >
              <div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                  Edit another board
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: 2 }}>
                  Select a board to open it directly in edit mode.
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowBoardListPopup(false)}
                aria-label="Close board list"
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  lineHeight: 1,
                  padding: 4,
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                overflowY: 'auto',
                padding: 10,
              }}
            >
              {userBoards.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
                  No boards found.
                </div>
              ) : (
                userBoards
                  .slice()
                  .sort((a, b) =>
                    String(a.name || '').localeCompare(String(b.name || ''))
                  )
                  .map((board) => {
                    const isCurrent = String(board.id) === String(id);

                    return (
                      <button
                        key={board.id}
                        type="button"
                        onClick={() => openBoardForEditing(board.id)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          border: isCurrent
                            ? '2px solid #007bff'
                            : '1px solid #ddd',
                          background: isCurrent ? '#eef6ff' : '#fff',
                          borderRadius: 8,
                          padding: '11px 12px',
                          marginBottom: 8,
                          cursor: isCurrent ? 'default' : 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: isCurrent ? 700 : 500,
                            color: '#222',
                          }}
                        >
                          {board.name || `Board #${board.id}`}
                        </span>

                        {isCurrent && (
                          <span
                            style={{
                              fontSize: '0.78rem',
                              color: '#007bff',
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Current board
                          </span>
                        )}
                      </button>
                    );
                  })
              )}
            </div>
          </div>
        </div>
      )}

      {showSetupPopup && (
        <Popup
          isOpen={showSetupPopup}
          onClose={() => setShowSetupPopup(false)}
          mode="edit"
          initialData={{
            name: boardConfig.name || boardName,
            columns: boardConfig.columns,
            rows: boardConfig.rows,
            scrollable: boardConfig.scrollable,
            square_buttons: boardConfig.square_buttons,
            id: Number(id),
            theme: boardConfig.theme || 'default',
            is_core_board: boardConfig.is_core_board,
            core_default_board_id: boardConfig.core_default_board_id,
          }}
          availableBoards={userBoards}
          onSubmit={handleBoardUpdate}
        />
      )}
    </>
  );
};

export default BoardEdit;
